


import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  alpha,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { TextField } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import BuildIcon from "@mui/icons-material/Build";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { useQuery, keepPreviousData } from "@tanstack/react-query";

import DashboardServices from "../../../service/dashboard";
import SpotServices from "../../../service/spot";
import DepartmentServices from "../../../service/department";
import AssetService from "../../../service/asset";

/* -------------------------------------------------------------------------- */
/*  Design tokens (shared with DashboardCompliance & Overview)                 */
/* -------------------------------------------------------------------------- */
const COLORS = {
  primary: "#0F4C75",
  primaryLight: "#3282B8",
  accent: "#1B9C85",
  success: "#2E7D32",
  warning: "#ED6C02",
  error: "#D32F2F",
  surface: "#FFFFFF",
  background: "#F4F7FA",
  border: "#E2E8F0",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  muted: "#94A3B8",
};

const PIE_COLORS = {
  overdue: COLORS.error,
  dueSoon: COLORS.warning,
  good: COLORS.success,
};

const PERIODS = [
  "This Week",
  "This Month",
  "This Year",
  "Custom Date",
];

/* -------------------------------------------------------------------------- */
/*  Shared UI Helper Components (Matching Compliance)                        */
/* -------------------------------------------------------------------------- */
const MetricCard = ({
  label,
  value,
  hint,
  icon: Icon,
  color = COLORS.primary,
}) => (
  <Card
    elevation={0}
    sx={{
      border: `1px solid ${COLORS.border}`,
      borderRadius: 2.5,
      height: "100%",
      backgroundColor: COLORS.surface,
      transition: "box-shadow 0.2s ease",
      "&:hover": {
        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.06)",
      },
    }}
  >
    <CardContent
      sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              color: COLORS.textSecondary,
              fontWeight: 500,
              mb: 0.75,
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="h3"
            sx={{
              color,
              fontWeight: 700,
              letterSpacing: "-0.03em",
              lineHeight: 1.1,
              fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
            }}
          >
            {value}
          </Typography>
          {hint && (
            <Typography
              variant="caption"
              sx={{ color: COLORS.muted, mt: 0.75, display: "block" }}
            >
              {hint}
            </Typography>
          )}
        </Box>
        {Icon && (
          <Box
            sx={{
              width: { xs: 36, sm: 42 },
              height: { xs: 36, sm: 42 },
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              backgroundColor: alpha(color, 0.1),
              color,
              flexShrink: 0,
              ml: 1,
            }}
          >
            <Icon sx={{ fontSize: { xs: 18, sm: 20 } }} />
          </Box>
        )}
      </Stack>
    </CardContent>
  </Card>
);

const SectionCard = ({ title, description, children, action, sx }) => (
  <Card
    elevation={0}
    sx={{
      border: `1px solid ${COLORS.border}`,
      borderRadius: 2.5,
      mb: 2,
      backgroundColor: COLORS.surface,
      boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
      ...sx,
    }}
  >
    <CardContent
      sx={{
        p: { xs: 2, sm: 2.5, md: 2.75 },
        "&:last-child": { pb: { xs: 2, sm: 2.5, md: 2.75 } },
      }}
    >
      {(title || action) && (
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          mb={2}
          spacing={1}
        >
          <Box sx={{ minWidth: 0 }}>
            {title && (
              <Typography
                variant="subtitle1"
                fontWeight={650}
                sx={{ color: COLORS.textPrimary, letterSpacing: "-0.01em" }}
              >
                {title}
              </Typography>
            )}
            {description && (
              <Typography
                variant="caption"
                sx={{ color: COLORS.textSecondary, display: "block", mt: 0.25 }}
              >
                {description}
              </Typography>
            )}
          </Box>
          {action}
        </Stack>
      )}
      {children}
    </CardContent>
  </Card>
);

const EmptyState = ({ message }) => (
  <Box sx={{ textAlign: "center", py: 4, px: 2 }}>
    <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
      {message}
    </Typography>
  </Box>
);

/* -------------------------------------------------------------------------- */
/*  Main Prediction Dashboard Component                                       */
/* -------------------------------------------------------------------------- */
export const DashboardPrediction = () => {
  // Selected filter state
  const [selectedSite, setSelectedSite] = useState("ALL");
  const [selectedDept, setSelectedDept] = useState("ALL");
  const [selectedSubDept, setSelectedSubDept] = useState("ALL");
  const [selectedCategory, setSelectedCategory] = useState("ALL");
  const [selectedPeriod, setSelectedPeriod] = useState("This Month");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // React Query for filters (matching DashboardCompliance)
  const spotQuery = useQuery({
    queryKey: ["prediction-spots"],
    queryFn: () => SpotServices.allSpots(),
  });

  const departmentQuery = useQuery({
    queryKey: ["prediction-departments"],
    queryFn: () => DepartmentServices.allDepartments(),
  });

  const subDepartmentQuery = useQuery({
    queryKey: ["prediction-subdepartments", selectedDept],
    queryFn: () => {
      if (!selectedDept || selectedDept === "ALL") return [];
      return DepartmentServices.subDepartmentByDepartment({ id: selectedDept });
    },
    enabled: !!selectedDept && selectedDept !== "ALL",
  });

  const categoryQuery = useQuery({
    queryKey: ["prediction-categories"],
    queryFn: () => AssetService.allAssetLabel("category"),
  });

  // Main service prediction query with keepPreviousData for smooth filter switching
  const predictionQuery = useQuery({
    queryKey: [
      "dashboard-prediction",
      selectedSite,
      selectedDept,
      selectedSubDept,
      selectedCategory,
      selectedPeriod,
      customStartDate,
      customEndDate,
    ],
    queryFn: () =>
      DashboardServices.getDashboardServicePrediction({
        site: selectedSite,
        department: selectedDept,
        subDepartment: selectedSubDept,
        category: selectedCategory,
        period: selectedPeriod,
        startDate: customStartDate,
        endDate: customEndDate,
      }),
    placeholderData: keepPreviousData,
  });

  const sites = spotQuery.data || [];
  const departments = departmentQuery.data || [];
  const subDepartments = subDepartmentQuery.data || [];
  const categories = categoryQuery.data || [];

  const predictionData = predictionQuery.data || {
    avgSmrLeftByDepartment: [],
    overallStatus: { overdue: 0, dueSoon: 0, good: 0 },
    top10OverdueAssets: [],
    totalAssets: 0,
  };

  const filteredDepartments =
    selectedSite === "ALL"
      ? departments
      : departments.filter(
          (d) =>
            d.site === Number(selectedSite) ||
            d.siteId === Number(selectedSite) ||
            (d.site && d.site.id === Number(selectedSite))
        );

  const pieData = [
    { name: "Overdue", value: predictionData.overallStatus.overdue, key: "overdue" },
    { name: "Due Soon", value: predictionData.overallStatus.dueSoon, key: "dueSoon" },
    { name: "Good", value: predictionData.overallStatus.good, key: "good" },
  ].filter(
    (item) =>
      item.value > 0 ||
      (predictionData.overallStatus.overdue === 0 &&
        predictionData.overallStatus.dueSoon === 0 &&
        predictionData.overallStatus.good === 0)
  );

  const isLoading = predictionQuery.isLoading;
  const isFetching = predictionQuery.isFetching;

  if (isLoading) {
    return (
      <Box>
        <Grid container spacing={2} mb={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={110} sx={{ borderRadius: 2.5 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2.5 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh" }}>
      {/* Top Progress bar when fetching background query updates */}
      {isFetching && (
        <LinearProgress
          sx={{
            mb: 2.5,
            borderRadius: 1,
            height: 3,
            backgroundColor: alpha(COLORS.primary, 0.08),
            "& .MuiLinearProgress-bar": {
              backgroundColor: COLORS.primaryLight,
            },
          }}
        />
      )}

      {/* Metric Summary Bar */}
      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Total fleet assets"
            value={predictionData.totalAssets}
            icon={BuildIcon}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Overdue assets"
            value={predictionData.overallStatus.overdue}
            icon={ReportProblemIcon}
            color={predictionData.overallStatus.overdue > 0 ? COLORS.error : COLORS.success}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Due soon assets"
            value={predictionData.overallStatus.dueSoon}
            icon={WarningAmberIcon}
            color={COLORS.warning}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Good condition assets"
            value={predictionData.overallStatus.good}
            icon={CheckCircleOutlineIcon}
            color={COLORS.success}
          />
        </Grid>
      </Grid>

      {/* Filter Controls (Matching Compliance styling & responsive stack) */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        mb={3}
        alignItems={{ xs: "stretch", sm: "flex-end" }}
        flexWrap="wrap"
        useFlexGap
      >
        {/* Site / Spot Filter */}
        <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
          <InputLabel>Site (Spot)</InputLabel>
          <Select
            value={selectedSite}
            label="Site (Spot)"
            onChange={(e) => {
              setSelectedSite(e.target.value);
              setSelectedDept("ALL");
              setSelectedSubDept("ALL");
            }}
            sx={{
              borderRadius: 1.5,
              backgroundColor: COLORS.surface,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: COLORS.border,
              },
            }}
          >
            <MenuItem value="ALL">All sites</MenuItem>
            {sites.map((spot) => (
              <MenuItem key={spot.id} value={spot.id}>
                {spot.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Department Filter */}
        <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
          <InputLabel>Department</InputLabel>
          <Select
            value={selectedDept}
            label="Department"
            onChange={(e) => {
              setSelectedDept(e.target.value);
              setSelectedSubDept("ALL");
            }}
            sx={{
              borderRadius: 1.5,
              backgroundColor: COLORS.surface,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: COLORS.border,
              },
            }}
          >
            <MenuItem value="ALL">All departments</MenuItem>
            {filteredDepartments.map((dept) => (
              <MenuItem key={dept.id} value={dept.id}>
                {dept.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Sub-Department Filter */}
        <FormControl
          size="small"
          disabled={!selectedDept || selectedDept === "ALL" || subDepartments.length === 0}
          sx={{ minWidth: { xs: "100%", sm: 160 } }}
        >
          <InputLabel>Sub-department</InputLabel>
          <Select
            value={selectedSubDept}
            label="Sub-department"
            onChange={(e) => setSelectedSubDept(e.target.value)}
            sx={{
              borderRadius: 1.5,
              backgroundColor: COLORS.surface,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: COLORS.border,
              },
            }}
          >
            <MenuItem value="ALL">All sub-departments</MenuItem>
            {subDepartments.map((sub) => (
              <MenuItem key={sub.id} value={sub.id}>
                {sub.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Asset Category Filter */}
        <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
          <InputLabel>Asset category</InputLabel>
          <Select
            value={selectedCategory}
            label="Asset category"
            onChange={(e) => setSelectedCategory(e.target.value)}
            sx={{
              borderRadius: 1.5,
              backgroundColor: COLORS.surface,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: COLORS.border,
              },
            }}
          >
            <MenuItem value="ALL">All categories</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Period Filter */}
        <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={selectedPeriod}
            label="Period"
            onChange={(e) => setSelectedPeriod(e.target.value)}
            sx={{
              borderRadius: 1.5,
              backgroundColor: COLORS.surface,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: COLORS.border,
              },
            }}
          >
            {PERIODS.map((period) => (
              <MenuItem key={period} value={period}>
                {period}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Custom Date Inputs */}
        {selectedPeriod === "Custom Date" && (
          <>
            <TextField
              size="small"
              type="date"
              label="Start Date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                minWidth: { xs: "100%", sm: 160 },
                backgroundColor: COLORS.surface,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  "& fieldset": { borderColor: COLORS.border },
                },
              }}
            />
            <TextField
              size="small"
              type="date"
              label="End Date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                minWidth: { xs: "100%", sm: 160 },
                backgroundColor: COLORS.surface,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  "& fieldset": { borderColor: COLORS.border },
                },
              }}
            />
          </>
        )}

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => predictionQuery.refetch()}
          disabled={isFetching}
          sx={{
            borderRadius: 1.5,
            borderColor: COLORS.border,
            color: COLORS.textPrimary,
            fontWeight: 600,
            backgroundColor: COLORS.surface,
            px: 2,
            height: 40,
            "&:hover": {
              borderColor: COLORS.primaryLight,
              backgroundColor: alpha(COLORS.primary, 0.04),
            },
          }}
        >
          Refresh
        </Button>
      </Stack>

      {/* Charts Section */}
      <Grid container spacing={2} mb={1}>
        {/* Avg SMR Left by Department Bar Chart */}
        <Grid item xs={12} md={7}>
          <SectionCard
            title="Average SMR left by department"
            description="Mean remaining SMR hours prior to required service"
          >
            {predictionData.avgSmrLeftByDepartment.length > 0 ? (
              <Box sx={{ width: "100%", height: { xs: 240, sm: 280 } }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={predictionData.avgSmrLeftByDepartment}
                    margin={{ top: 10, right: 20, left: 0, bottom: 25 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.border} />
                    <XAxis
                      dataKey="department"
                      angle={-20}
                      textAnchor="end"
                      interval={0}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <RechartsTooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: `1px solid ${COLORS.border}`,
                      }}
                    />
                    <Bar
                      dataKey="avgSmrLeft"
                      fill={COLORS.primaryLight}
                      name="Avg SMR Left"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <EmptyState message="No SMR data available for selected filters." />
            )}
          </SectionCard>
        </Grid>

        {/* Overall Status Pie Chart */}
        <Grid item xs={12} md={5}>
          <SectionCard
            title="Overall service status"
            description="Asset health breakdown by service urgency"
          >
            <Box sx={{ width: "100%", height: { xs: 240, sm: 280 } }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {pieData.map((entry) => (
                      <Cell key={`cell-${entry.key}`} fill={PIE_COLORS[entry.key]} />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(val, name) => [`${val} Assets`, name]}
                    contentStyle={{
                      borderRadius: 8,
                      border: `1px solid ${COLORS.border}`,
                    }}
                  />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Top 10 Overdue Assets Table */}
      <SectionCard
        title="Top 10 overdue assets"
        description="Assets exceeding service interval ranked by highest overdue SMR"
      >
        {predictionData.top10OverdueAssets.length > 0 ? (
          <TableContainer sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <Table size="small" sx={{ minWidth: 680 }}>
              <TableHead>
                <TableRow>
                  {[
                    "Asset ID",
                    "VIN / Serial",
                    "Site",
                    "Department",
                    "Sub-department",
                    "Category",
                    "Current SMR",
                    "Service interval",
                    "SMR left",
                    "Status",
                  ].map((h, i) => (
                    <TableCell
                      key={h}
                      align={i >= 6 && i <= 8 ? "right" : i === 9 ? "center" : "left"}
                      sx={{
                        fontWeight: 650,
                        color: COLORS.textSecondary,
                        borderBottom: `1px solid ${COLORS.border}`,
                        py: 1.25,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {predictionData.top10OverdueAssets.map((asset) => (
                  <TableRow key={asset.id} hover>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>{asset.assetId}</TableCell>
                    <TableCell sx={{ color: COLORS.textSecondary }}>{asset.vinNumber || "—"}</TableCell>
                    <TableCell sx={{ color: COLORS.textSecondary }}>{asset.site || "—"}</TableCell>
                    <TableCell sx={{ color: COLORS.textSecondary }}>{asset.department || "—"}</TableCell>
                    <TableCell sx={{ color: COLORS.textSecondary }}>{asset.subDepartment || "—"}</TableCell>
                    <TableCell sx={{ color: COLORS.textSecondary }}>{asset.category || "—"}</TableCell>
                    <TableCell align="right">
                      {Number.isFinite(asset.smr) ? asset.smr.toLocaleString() : "—"}
                    </TableCell>
                    <TableCell align="right">
                      {Number.isFinite(asset.serviceInterval)
                        ? asset.serviceInterval.toLocaleString()
                        : "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ color: COLORS.error, fontWeight: 700 }}>
                      {Number.isFinite(asset.smrLeft) ? asset.smrLeft.toLocaleString() : "—"}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label="Overdue"
                        size="small"
                        sx={{
                          backgroundColor: COLORS.error,
                          color: "white",
                          fontWeight: 600,
                          height: 22,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <EmptyState message="No overdue assets match the selected filters." />
        )}
      </SectionCard>
    </Box>
  );
};

